import React, { useContext, useEffect, useState } from 'react'
import { SRD } from 'srd'
import { SelectedCountryContext } from 'domain/SelectedCountryContext'
import { useRemoteData } from 'lib/RemoteData'
import { API_HOST } from 'lib'
import { Dialog } from '../../components/Dialog'
import { LoadingSpinner } from '../../components/LoadingSpinner'
import {
  dismissManualAction,
  ManualActionInboxEntry,
  ManualActionType,
  resolveManualAction,
} from '../../services/ManualActionService'

const formatDate = (isoString: string): string => {
  const date = new Date(isoString)
  return date.toLocaleDateString('en-CA') // YYYY-MM-DD format
}

const actionTypeLabel = (type: ManualActionType): string => {
  switch (type) {
    case 'RESTORE_OVERRIDES':
      return 'Restore overrides'
  }
}

const primaryActionLabel = (type: ManualActionType): string => {
  switch (type) {
    case 'RESTORE_OVERRIDES':
      return 'Restore overrides'
  }
}

type ToastState = {
  type: 'success' | 'error'
  message: string
} | null

const Toast = ({ toast, onDismiss }: { toast: ToastState; onDismiss: () => void }) => {
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(onDismiss, 5000)
      return () => clearTimeout(timer)
    }
  }, [toast, onDismiss])

  if (!toast) return null

  const className = toast.type === 'success' ? 'alert alert-success' : 'alert alert-danger'

  return (
    <div className={`${className} mb-md flex justify-between items-center`}>
      <span>{toast.message}</span>
      <button className="ml-md text-lg font-bold" onClick={onDismiss} aria-label="Dismiss">
        &times;
      </button>
    </div>
  )
}

const ConfirmResolveDialog = ({
  item,
  isProcessing,
  onCancel,
  onConfirm,
}: {
  item: ManualActionInboxEntry
  isProcessing: boolean
  onCancel: () => void
  onConfirm: () => void
}) => (
  <Dialog onClose={onCancel}>
    <div className="card p-lg" style={{ maxWidth: '32em' }}>
      <h2 className="text-xl mb-md">{actionTypeLabel(item.type)}?</h2>
      <p className="text-sm text-gray-600 mb-sm">
        Serial: <strong>{item.serialNumber}</strong>
      </p>
      <p className="mb-md">{item.reason}</p>
      <div className="flex justify-end gap-sm">
        <button className="btn btn-secondary" onClick={onCancel} disabled={isProcessing}>
          Cancel
        </button>
        <button className="btn btn-primary-dark" onClick={onConfirm} disabled={isProcessing}>
          {isProcessing ? 'Processing...' : primaryActionLabel(item.type)}
        </button>
      </div>
    </div>
  </Dialog>
)

type StatusContentProps = {
  items: ManualActionInboxEntry[]
  refresh: () => void
  toast: ToastState
  setToast: (toast: ToastState) => void
}

const StatusContent = ({ items, refresh, toast, setToast }: StatusContentProps) => {
  const { selectedCountry } = useContext(SelectedCountryContext)
  const [activeItem, setActiveItem] = useState<ManualActionInboxEntry | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [isProcessing, setIsProcessing] = useState(false)
  const [processingId, setProcessingId] = useState<string | null>(null)

  const visibleItems = items
  const visibleSelectedIds = visibleItems.map((i) => i.id).filter((id) => selectedIds.has(id))
  const allVisibleSelected = visibleItems.length > 0 && visibleSelectedIds.length === visibleItems.length
  const someVisibleSelected = visibleSelectedIds.length > 0 && !allVisibleSelected

  const toggleSelection = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })

  const toggleSelectAll = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (allVisibleSelected) {
        for (const item of visibleItems) {
          next.delete(item.id)
        }
      } else {
        for (const item of visibleItems) {
          next.add(item.id)
        }
      }
      return next
    })

  const handleResolve = async (item: ManualActionInboxEntry) => {
    setIsProcessing(true)
    setProcessingId(item.id)
    try {
      const result = await resolveManualAction(item.id)
      setToast({
        type: 'success',
        message: `Restored ${result.restoredModifications} modifications for ${item.serialNumber}`,
      })
      setActiveItem(null)
      refresh()
    } catch (err) {
      setToast({
        type: 'error',
        message: `Failed to resolve ${item.serialNumber}: ${err instanceof Error ? err.message : 'Unknown error'}`,
      })
    } finally {
      setIsProcessing(false)
      setProcessingId(null)
    }
  }

  const handleDismiss = async (id: string, serialNumber: string) => {
    setProcessingId(id)
    try {
      await dismissManualAction(id)
      refresh()
    } catch (err) {
      setToast({
        type: 'error',
        message: `Failed to ignore ${serialNumber}: ${err instanceof Error ? err.message : 'Unknown error'}`,
      })
    } finally {
      setProcessingId(null)
    }
  }

  const handleBulkResolve = async () => {
    setIsProcessing(true)
    let successCount = 0
    let failCount = 0

    for (const id of visibleSelectedIds) {
      const item = visibleItems.find((i) => i.id === id)
      if (!item) continue

      setProcessingId(id)
      try {
        await resolveManualAction(id)
        successCount++
      } catch (err) {
        failCount++
        setToast({
          type: 'error',
          message: `Failed to resolve ${item.serialNumber}: ${err instanceof Error ? err.message : 'Unknown error'}`,
        })
      }
    }

    setIsProcessing(false)
    setProcessingId(null)
    setSelectedIds(new Set())
    refresh()

    if (successCount > 0 && failCount === 0) {
      setToast({ type: 'success', message: `Resolved ${successCount} item(s)` })
    } else if (successCount > 0 && failCount > 0) {
      setToast({ type: 'error', message: `Resolved ${successCount}, failed ${failCount}` })
    }
  }

  const handleBulkDismiss = async () => {
    setIsProcessing(true)
    let successCount = 0
    let failCount = 0

    for (const id of visibleSelectedIds) {
      const item = visibleItems.find((i) => i.id === id)
      if (!item) continue

      setProcessingId(id)
      try {
        await dismissManualAction(id)
        successCount++
      } catch (err) {
        failCount++
        setToast({
          type: 'error',
          message: `Failed to ignore ${item.serialNumber}: ${err instanceof Error ? err.message : 'Unknown error'}`,
        })
      }
    }

    setIsProcessing(false)
    setProcessingId(null)
    setSelectedIds(new Set())
    refresh()

    if (successCount > 0 && failCount === 0) {
      setToast({ type: 'success', message: `Ignored ${successCount} item(s)` })
    } else if (successCount > 0 && failCount > 0) {
      setToast({ type: 'error', message: `Ignored ${successCount}, failed ${failCount}` })
    }
  }

  const emptyMessage = `No manual interventions for ${selectedCountry}.`

  return (
    <>
      <Toast toast={toast} onDismiss={() => setToast(null)} />

      <div className="card">
        <div className="p-md border-b">
          <h2 className="text-xl">Manual interventions</h2>
          <p className="text-sm text-gray-600 mt-sm">
            Machines in {selectedCountry} that require a manual step. Resolve to apply the suggested fix, or ignore to
            dismiss the item.
          </p>
        </div>

        {visibleItems.length === 0 ? (
          <div className="p-md text-gray-600" data-testid="status-empty">
            {emptyMessage}
          </div>
        ) : (
          <>
            <div className="p-md border-b flex items-center gap-sm">
              <span className="text-sm text-gray-600" data-testid="selection-count">
                {visibleSelectedIds.length} selected
              </span>
              <button
                className="btn btn-primary-dark"
                disabled={visibleSelectedIds.length === 0 || isProcessing}
                onClick={handleBulkResolve}
                data-testid="resolve-selected"
              >
                {isProcessing ? 'Processing...' : 'Resolve selected'}
              </button>
              <button
                className="btn btn-secondary"
                disabled={visibleSelectedIds.length === 0 || isProcessing}
                onClick={handleBulkDismiss}
                data-testid="ignore-selected"
              >
                {isProcessing ? 'Processing...' : 'Ignore selected'}
              </button>
            </div>
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: '3.5rem', minWidth: '2.5rem', paddingLeft: '1rem', paddingRight: '0.5rem' }}>
                    <input
                      type="checkbox"
                      aria-label="Select all"
                      data-testid="select-all"
                      checked={allVisibleSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someVisibleSelected
                      }}
                      onChange={toggleSelectAll}
                      disabled={isProcessing}
                    />
                  </th>
                  <th style={{ minWidth: '10em' }}>Serial</th>
                  <th style={{ minWidth: '8em' }}>Date</th>
                  <th style={{ minWidth: '12em' }}>Action type</th>
                  <th style={{ minWidth: '24em' }}>Reason</th>
                  <th style={{ minWidth: '14em' }}></th>
                </tr>
              </thead>
              <tbody>
                {visibleItems.map((item) => (
                  <tr
                    key={item.id}
                    data-testid={`action-row-${item.id}`}
                    className={processingId === item.id ? 'opacity-50' : ''}
                  >
                    <td style={{ paddingLeft: '1rem', paddingRight: '0.5rem' }}>
                      <input
                        type="checkbox"
                        aria-label={`Select ${item.serialNumber}`}
                        data-testid={`select-${item.id}`}
                        checked={selectedIds.has(item.id)}
                        onChange={() => toggleSelection(item.id)}
                        disabled={isProcessing}
                      />
                    </td>
                    <td>{item.serialNumber}</td>
                    <td>{formatDate(item.createdAt)}</td>
                    <td>{actionTypeLabel(item.type)}</td>
                    <td>{item.reason}</td>
                    <td>
                      <div className="inline-flex gap-sm">
                        <button
                          className="btn btn-primary-dark"
                          onClick={() => setActiveItem(item)}
                          data-testid={`resolve-${item.id}`}
                          disabled={isProcessing}
                        >
                          Resolve...
                        </button>
                        <button
                          className="btn btn-secondary"
                          onClick={() => handleDismiss(item.id, item.serialNumber)}
                          data-testid={`ignore-${item.id}`}
                          disabled={isProcessing}
                        >
                          Ignore
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      {activeItem && (
        <ConfirmResolveDialog
          item={activeItem}
          isProcessing={isProcessing}
          onCancel={() => setActiveItem(null)}
          onConfirm={() => handleResolve(activeItem)}
        />
      )}
    </>
  )
}

export const Status = () => {
  const { selectedCountry } = useContext(SelectedCountryContext)
  const { data: remoteData, refresh } = useRemoteData<ManualActionInboxEntry[]>(
    `${API_HOST}/v1/manual-actions?country=${selectedCountry}`,
  )
  const [toast, setToast] = useState<ToastState>(null)

  return (
    <div>
      <h1 className="text-2xl mb-md">Status</h1>

      {SRD.match(
        {
          notAsked: () => <LoadingSpinner />,
          loading: () => <LoadingSpinner />,
          failure: (err) => (
            <div className="card">
              <div className="p-md">
                <div className="alert alert-danger">
                  Failed to load manual interventions
                  <div>{err.body?.title || err.statusText}</div>
                </div>
              </div>
            </div>
          ),
          success: (items) => <StatusContent items={items} refresh={refresh} toast={toast} setToast={setToast} />,
        },
        remoteData,
      )}
    </div>
  )
}
